import React, { FC, lazy, memo, Suspense, useEffect, useMemo } from 'react'

import { useSetFocus } from '@please/lrud'

import { BroadcastMain } from '@/components/Broadcast/BroadcastMain'
import { eventAction, eventObject } from '@/constants/analytics'
import { VideoPlaybackStatus } from '@/constants/common'
import { popupStatus } from '@/constants/popup'
import useStore from '@/store/useStore'
import { VideoViewedType } from '@/types'
import { VideoPlayerProps } from '@/types/componentsPropsTypes'
import { ChannelType, EndActions } from '@/types/servicesTypes/configTypes'
import { sendAnalyticsEvent } from '@/utils/analyticsUtils'

const VideoPlayer: FC<VideoPlayerProps> = lazy(
  () => import('@/components/VideoPlayer/VideoPlayer'),
)

import {
  StageBackgroundContainer,
  StageBackgroundContainerFader,
  StageVideoContainer,
} from '../styles'

type Props = {
  src?: string | ChannelType
  srcType?: string
  id?: string
  slideTitle?: string
  isFaded?: boolean
  isVisible?: boolean
  broadcastId: string
  onEnded?: EndActions
  setVideoPlaybackStatus?: (value: Nullable<VideoPlaybackStatus>) => void
  focusIdPrefix?: string
  isPopup?: boolean
}

const StageBackgroundRaw = ({
  src,
  id,
  slideTitle,
  srcType,
  isFaded,
  isVisible,
  broadcastId,
  onEnded,
  setVideoPlaybackStatus,
  focusIdPrefix = '',
  isPopup,
}: Props) => {
  if (!src) {
    return <StageBackgroundContainerFader $isFaded={isFaded} />
  }

  return (
    <StageBackgroundContainer $isFaded={isFaded}>
      {typeof src === 'object' && (
        <BroadcastMain
          isPopup={false}
          broadcastId={broadcastId}
          focusIdPrefix={focusIdPrefix}
          isMuted={!isVisible}
          isStageHome={true}
          setVideoPlaybackStatus={setVideoPlaybackStatus}
        />
      )}
      {srcType?.toLocaleLowerCase() === 'mp4' && typeof src === 'string' && (
        <StageVideoPlayer
          src={src}
          id={id}
          slideTitle={slideTitle}
          format={srcType}
          isVisible={isVisible}
          onEnded={onEnded}
          setVideoPlaybackStatus={setVideoPlaybackStatus}
          focusIdPrefix={focusIdPrefix}
          isPopup={isPopup}
        />
      )}
      {srcType?.toLocaleLowerCase() === 'image' && typeof src === 'string' && (
        <img src={src} alt={''} />
      )}
      <StageBackgroundContainerFader $isFaded={isFaded} />
    </StageBackgroundContainer>
  )
}

export const StageBackground = memo(StageBackgroundRaw)

type StageVideoPlayerProps = {
  src?: string
  id?: string
  format: string
  slideTitle?: string
  isVisible?: boolean
  onEnded?: EndActions
  setVideoPlaybackStatus?: (value: Nullable<VideoPlaybackStatus>) => void
  focusIdPrefix?: string
  isPopup?: boolean
}

const StageVideoPlayer = ({
  src,
  id,
  format,
  slideTitle,
  isVisible,
  onEnded,
  setVideoPlaybackStatus,
  focusIdPrefix,
  isPopup,
}: StageVideoPlayerProps) => {
  const focusId = `${focusIdPrefix}_StageVideoPlayer`
  const isFullscreen = useStore((state) => state.player.isFullscreen)
  const setIsFullscreen = useStore((state) => state.player.setIsFullscreen)
  const event = useStore((state) => state.common.currentEvent)
  const mediaVideo = useStore((state) => state.common.mediaVideo)
  const youTubeVideo = useStore((state) => state.common.youTubeVideo)
  const pagePopupStatus = useStore((state) => state.popup.pagePopupStatus)

  const setFocus = useSetFocus()

  const isEventPopupOpen = Boolean(event)
  const isPagePopupOpen = pagePopupStatus !== popupStatus.CLOSED

  const isVideoDisplayed =
    !mediaVideo && !youTubeVideo && (isPopup || !isPagePopupOpen)
  const isVideoMuted = isEventPopupOpen || !isVisible

  useEffect(() => {
    if (isFullscreen) {
      setFocus(focusId)
    }
  }, [isFullscreen])

  const onExitButton = () => {
    setIsFullscreen(false)
    sendAnalyticsEvent({
      object: eventObject.VIDEO,
      action: eventAction.BACKGROUND_CLICK,
      video: {
        id: id,
        link: src,
      },
    })
  }

  const videoData: VideoViewedType = useMemo(
    () => ({ id, url: src, format, media_name: slideTitle || id }),
    [id, src, format, slideTitle],
  )
  return (
    <StageVideoContainer
      data-component="StageVideoContainer"
      focusId={focusId}
      $isFullscreen={isFullscreen}
      isTrap={isFullscreen}
      disabled={!isFullscreen}
    >
      {isVideoDisplayed && (
        <Suspense>
          <VideoPlayer
            src={src}
            videoData={videoData}
            focusIdPrefix={focusIdPrefix}
            withControls={isFullscreen}
            isMuted={isVideoMuted}
            autoplay={true}
            onEndedAction={onEnded}
            setVideoPlaybackStatus={setVideoPlaybackStatus}
            onExitButton={onExitButton}
            withUserInteraction={false}
            isStageHome
            inPagePlayer
          />
        </Suspense>
      )}
    </StageVideoContainer>
  )
}
